import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { detectorCorners, footprintOnPlane } from "../geometry/vxGeom";
import { COLOR_WITHIN_FOV, COLOR_OUT_OF_FOV } from "../constants/GeometryFOVConstants";
import "./CanvasPane.css";

const COLOR_INCLINED = 0xf97362;
const COLOR_COPLANAR = 0x5eead4;
const COLOR_GIMBAL = 0xf5a623;
const COLOR_BORDER = 0x4c4c4c;
const COLOR_AXIS_X = 0x5d7f2d;
const COLOR_AXIS_Y = 0x7a3d45;
const COLOR_AXIS_Z = 0x046ab2;
const COLOR_VOLUME_EDGE = 0x046ab2;
const COLOR_VOLUME_SURFACE = 0x25adde;
const COLOR_VOLUME_OUTSIDE = 0xff3158;

// Cylinder radius as a fraction of axis length (the group is later scaled
// uniformly via scale.setScalar to set that length, so this stays a
// constant proportion of it rather than a fixed world-space size).
const AXIS_THICKNESS = 0.005;

// A unit-length (along +Y) cylinder mesh, reoriented/colored for one axis.
function buildAxisCylinder(color, direction) {
  const geo = new THREE.CylinderGeometry(1, 1, 1, 8);
  geo.translate(0, 0.5, 0); // base at origin, tip at local +Y=1
  const mat = new THREE.MeshBasicMaterial({ color });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.scale.set(AXIS_THICKNESS, 1, AXIS_THICKNESS);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.clone().normalize());
  return mesh;
}

// Custom axes (replaces THREE.AxesHelper's default red/green/blue), colored
// by native axis (not world axis) so they match FovCanvas's top-down
// native-X/native-Y color scheme: world X = native X, world Z = native -Y,
// world Y = native Z (per toWorld's remap). Rendered as thin cylinders
// rather than THREE.Line since LineBasicMaterial's linewidth is ignored by
// most WebGL backends, making a "thicker axis" request otherwise a no-op.
function buildAxes() {
  const group = new THREE.Group();
  group.add(buildAxisCylinder(COLOR_AXIS_X, new THREE.Vector3(1, 0, 0)));
  group.add(buildAxisCylinder(COLOR_AXIS_Y, new THREE.Vector3(0, 0, -1)));
  group.add(buildAxisCylinder(COLOR_AXIS_Z, new THREE.Vector3(0, 1, 0)));
  return group;
}

// True-to-scale axis remap only (native Z -> world Y, native Y -> world -Z).
// No distance compression: any nonlinear per-point remap breaks either
// flatness (a rigid detector plane) or ray-straightness (source-to-corner
// lines), and both must hold for the picture to be geometrically trustworthy.
function toWorld([x, y, z]) {
  return [x, z, -y];
}

function buildRig(color) {
  const group = new THREE.Group();

  const srcGeo = new THREE.SphereGeometry(0.35, 16, 16);
  const srcMat = new THREE.MeshBasicMaterial({ color });
  const srcMesh = new THREE.Mesh(srcGeo, srcMat);
  group.add(srcMesh);

  const detGeo = new THREE.BufferGeometry();
  detGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(12), 3));
  detGeo.setIndex([0, 1, 2, 0, 2, 3]);
  const detMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.35,
    side: THREE.DoubleSide,
  });
  const detMesh = new THREE.Mesh(detGeo, detMat);
  group.add(detMesh);

  const detEdges = new THREE.LineLoop(
    new THREE.BufferGeometry().setAttribute("position", new THREE.BufferAttribute(new Float32Array(12), 3)),
    new THREE.LineBasicMaterial({ color })
  );
  group.add(detEdges);

  const rayPositions = new Float32Array(4 * 2 * 3);
  const rayGeo = new THREE.BufferGeometry();
  rayGeo.setAttribute("position", new THREE.BufferAttribute(rayPositions, 3));
  const rayMat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.5 });
  const rays = new THREE.LineSegments(rayGeo, rayMat);
  group.add(rays);

  // Four triangular faces from the source to the detector perimeter.
  const coneGeo = new THREE.BufferGeometry();
  coneGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(5 * 3), 3));
  coneGeo.setIndex([0, 1, 2, 0, 2, 3, 0, 3, 4, 0, 4, 1]);
  const coneMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.1,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const coneMesh = new THREE.Mesh(coneGeo, coneMat);
  group.add(coneMesh);

  const fpGeo = new THREE.BufferGeometry();
  fpGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(12), 3));
  fpGeo.setIndex([0, 1, 2, 0, 2, 3]);
  const fpMat = new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.4,
    side: THREE.DoubleSide,
  });
  const fpMesh = new THREE.Mesh(fpGeo, fpMat);
  group.add(fpMesh);

  const uAxisGeo = new THREE.BufferGeometry();
  uAxisGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
  const uAxis = new THREE.Line(uAxisGeo, new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.7 }));
  group.add(uAxis);

  const vAxisGeo = new THREE.BufferGeometry();
  vAxisGeo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
  const vAxis = new THREE.Line(vAxisGeo, new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.4 }));
  group.add(vAxis);

  return { group, srcMesh, detMesh, detEdges, rays, coneMesh, fpMesh, uAxis, vAxis };
}

// Reconstruction volume: a cuboid centered at the origin. volRes is mm/px,
// volX/volY/volZ are pixel counts along the native (X, Y, Z) axes, so real
// -world extents are volRes * volX/Y/Z (mm), same unit convention as detPitch.
function buildVolumeCuboid() {
  const geo = new THREE.BoxGeometry(1, 1, 1);
  const surfaceMat = new THREE.MeshBasicMaterial({
    color: COLOR_VOLUME_SURFACE,
    transparent: true,
    opacity: 0.5,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geo, surfaceMat);

  const edges = new THREE.LineSegments(
    new THREE.EdgesGeometry(geo),
    new THREE.LineBasicMaterial({ color: COLOR_VOLUME_EDGE })
  );
  mesh.add(edges);

  return { mesh, edges };
}

function buildOutsideVolume() {
  const geometry = new THREE.BoxGeometry(1, 1, 1);
  const material = new THREE.MeshBasicMaterial({
    color: COLOR_VOLUME_OUTSIDE,
    transparent: true,
    opacity: 0.82,
    depthTest: false,
    depthWrite: false,
  });
  const mesh = new THREE.InstancedMesh(geometry, material, 14000);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.count = 0;
  mesh.renderOrder = 2;
  return mesh;
}

function pointInsideCone(point, vec, halfU, halfV) {
  const S = new THREE.Vector3(vec[0], vec[1], vec[2]);
  const D = new THREE.Vector3(vec[3], vec[4], vec[5]);
  const U = new THREE.Vector3(vec[6], vec[7], vec[8]);
  const V = new THREE.Vector3(vec[9], vec[10], vec[11]);
  const ray = new THREE.Vector3(...point).sub(S);
  const normal = new THREE.Vector3().crossVectors(U, V);
  const denom = normal.dot(ray);
  if (Math.abs(denom) < 1e-12) return false;
  const t = normal.dot(D.clone().sub(S)) / denom;
  if (t <= 0) return false;
  const hit = S.clone().addScaledVector(ray, t).sub(D);
  const u = hit.dot(U) / U.lengthSq();
  const v = hit.dot(V) / V.lengthSq();
  return Math.abs(u) <= halfU + 1e-9 && Math.abs(v) <= halfV + 1e-9;
}

function updateOutsideVolume(
  mesh,
  vecs,
  halfU,
  halfV,
  volRes,
  volX,
  volY,
  volZ,
  offsetVolX = 0,
  offsetVolY = 0,
  offsetVolZ = 0
) {
  const sizeX = volRes * volX, sizeY = volRes * volY, sizeZ = volRes * volZ;
  if (!vecs.length || sizeX <= 0 || sizeY <= 0 || sizeZ <= 0) { mesh.count = 0; return; }
  const longest = Math.max(sizeX, sizeY, sizeZ), base = 22;
  const nx = Math.max(4, Math.round(base * sizeX / longest));
  const ny = Math.max(4, Math.round(base * sizeY / longest));
  const nz = Math.max(4, Math.round(base * sizeZ / longest));
  const dx = sizeX / nx, dy = sizeY / ny, dz = sizeZ / nz;
  const matrix = new THREE.Matrix4(), position = new THREE.Vector3();
  const scale = new THREE.Vector3(dx * 0.94, dz * 0.94, dy * 0.94);
  const quaternion = new THREE.Quaternion();
  let count = 0;
  for (let iz = 0; iz < nz; iz++) {
    const z = offsetVolZ - sizeZ / 2 + (iz + 0.5) * dz;
    for (let iy = 0; iy < ny; iy++) {
      const y = offsetVolY - sizeY / 2 + (iy + 0.5) * dy;
      for (let ix = 0; ix < nx; ix++) {
        const x = offsetVolX - sizeX / 2 + (ix + 0.5) * dx;
        // A cell belongs to the outside region when any of its eight corners
        // misses any acquisition cone. Centre-only sampling can miss a thin
        // violation along the volume boundary.
        const cellFullyInside = [-0.5, 0.5].every((sx) =>
          [-0.5, 0.5].every((sy) =>
            [-0.5, 0.5].every((sz) =>
              vecs.every((vec) => pointInsideCone(
                [x + sx * dx, y + sy * dy, z + sz * dz],
                vec,
                halfU,
                halfV
              ))
            )
          )
        );
        if (cellFullyInside) continue;
        const [wx, wy, wz] = toWorld([x, y, z]);
        position.set(wx, wy, wz);
        matrix.compose(position, quaternion, scale);
        mesh.setMatrixAt(count++, matrix);
      }
    }
  }
  mesh.count = count;
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingSphere();
}
function updateVolumeCuboid(volume, volRes, volX, volY, volZ, offsetVolX = 0, offsetVolY = 0, offsetVolZ = 0) {
  // native (X, Y, Z) extents in mm -> world (X, Z, Y) via the same toWorld remap.
  const sizeX = volRes * volX;
  const sizeYNative = volRes * volY;
  const sizeZNative = volRes * volZ;
  volume.mesh.scale.set(sizeX, sizeZNative, sizeYNative);
  const [wx, wy, wz] = toWorld([offsetVolX, offsetVolY, offsetVolZ]);
  volume.mesh.position.set(wx, wy, wz);
}

function setPos(array, i, [x, y, z]) {
  array[i * 3] = x;
  array[i * 3 + 1] = y;
  array[i * 3 + 2] = z;
}

function extentsOf(vecs, halfU, halfV) {
  let nearExtent = 0;
  let farExtent = 0;
  for (const vec of vecs) {
    const S = [vec[0], vec[1], vec[2]];
    nearExtent = Math.max(nearExtent, Math.hypot(S[0], S[1], S[2]));
    const corners = detectorCorners(vec, halfU, halfV);
    for (const c of corners) {
      farExtent = Math.max(farExtent, Math.hypot(c[0], c[1], c[2]));
    }
  }
  return { nearExtent, farExtent: Math.max(farExtent, nearExtent) };
}

// Unit view directions for the axis-aligned view buttons, in world space
// (toWorld: native X -> world X, native Y -> world -Z, native Z -> world Y).
const VIEW_DIRECTIONS = {
  "+X": [1, 0, 0],
  "-X": [-1, 0, 0],
  "+Y": [0, 0, -1],
  "-Y": [0, 0, 1],
  "+Z": [0, 1, 0],
  "-Z": [0, -1, 0],
};

// Fit the camera + grid/axes scale to either the near (source/object-plane)
// region or the overall (source-to-detector) extent. "near" keeps the
// cone-tip legible even when SOD is 1000x smaller than SDD; "overall" pulls
// back far enough to see the whole rig, source and detector alike. An
// optional `direction` (one of VIEW_DIRECTIONS' keys) points the camera
// straight down that native axis instead of the default diagonal angle,
// using the same distance/scale math so the framing stays consistent with
// the FOV/Overall buttons.
function fitCamera(st, nearExtent, farExtent, mode, direction = null) {
  if (nearExtent <= 0) return;
  const extent = mode === "overall" ? farExtent : nearExtent;

  st.gridHelper.scale.setScalar(farExtent * 2);
  st.axesHelper.scale.setScalar(Math.max(extent * 0.3, nearExtent * 1.5));
  st.origin.scale.setScalar(nearExtent * 0.02);

  const camDist = mode === "overall" ? farExtent * 1.8 : nearExtent * 3;
  const dir = direction ? VIEW_DIRECTIONS[direction] : null;
  if (dir) {
    st.camera.position.set(dir[0] * camDist, dir[1] * camDist, dir[2] * camDist);
  } else {
    st.camera.position.set(camDist * 0.6, camDist * 0.5, camDist * 0.6);
  }
  st.camera.near = Math.max(nearExtent * 0.005, 1e-4);
  st.camera.far = farExtent * 20;
  st.camera.updateProjectionMatrix();
  st.controls.target.set(0, 0, 0);
  st.controls.minDistance = nearExtent * 0.05;
  st.controls.maxDistance = farExtent * 10;
  st.controls.update();

  const srcRadius = nearExtent * 0.03;
  for (const rig of [st.inclinedRig, st.coplanarRig, st.gimbalRig]) {
    rig.srcMesh.geometry.dispose();
    rig.srcMesh.geometry = new THREE.SphereGeometry(srcRadius, 16, 16);
  }
}

function updateRig(rig, vec, halfU, halfV) {
  const S = [vec[0], vec[1], vec[2]];
  const D = [vec[3], vec[4], vec[5]];
  const U = [vec[6], vec[7], vec[8]];
  const V = [vec[9], vec[10], vec[11]];
  const corners = detectorCorners(vec, halfU, halfV);

  const Sw = toWorld(S);
  rig.srcMesh.position.set(Sw[0], Sw[1], Sw[2]);

  const cornersW = corners.map(toWorld);
  const detPos = rig.detMesh.geometry.attributes.position.array;
  const edgePos = rig.detEdges.geometry.attributes.position.array;
  cornersW.forEach((c, i) => {
    setPos(detPos, i, c);
    setPos(edgePos, i, c);
  });
  rig.detMesh.geometry.attributes.position.needsUpdate = true;
  rig.detEdges.geometry.attributes.position.needsUpdate = true;

  const rayPos = rig.rays.geometry.attributes.position.array;
  cornersW.forEach((c, i) => {
    setPos(rayPos, i * 2, Sw);
    setPos(rayPos, i * 2 + 1, c);
  });
  rig.rays.geometry.attributes.position.needsUpdate = true;

  const conePos = rig.coneMesh.geometry.attributes.position.array;
  setPos(conePos, 0, Sw);
  cornersW.forEach((corner, i) => setPos(conePos, i + 1, corner));
  rig.coneMesh.geometry.attributes.position.needsUpdate = true;
  rig.coneMesh.geometry.computeBoundingSphere();

  const fp = footprintOnPlane(vec, halfU, halfV, 0);
  const fpPos = rig.fpMesh.geometry.attributes.position.array;
  if (fp) {
    fp.forEach(([x, y], i) => setPos(fpPos, i, toWorld([x, y, 0])));
    rig.fpMesh.visible = true;
  } else {
    rig.fpMesh.visible = false;
  }
  rig.fpMesh.geometry.attributes.position.needsUpdate = true;

  const Dw = toWorld(D);
  const uTipW = toWorld([D[0] + U[0] * halfU, D[1] + U[1] * halfU, D[2] + U[2] * halfU]);
  const vTipW = toWorld([D[0] + V[0] * halfV, D[1] + V[1] * halfV, D[2] + V[2] * halfV]);

  const uPos = rig.uAxis.geometry.attributes.position.array;
  setPos(uPos, 0, Dw);
  setPos(uPos, 1, uTipW);
  rig.uAxis.geometry.attributes.position.needsUpdate = true;

  const vPos = rig.vAxis.geometry.attributes.position.array;
  setPos(vPos, 0, Dw);
  setPos(vPos, 1, vTipW);
  rig.vAxis.geometry.attributes.position.needsUpdate = true;
}

const Scene3D = forwardRef(function Scene3D(
  {
    inclinedVecs,
    coplanarVecs,
    gimbalVecs,
    showInclined,
    showCoplanar,
    showGimbal,
    halfU,
    halfV,
    theta,
    numAngles,
    angleStart,
    angleEnd,
    volRes,
    volX,
    volY,
    volZ,
    offsetVolX = 0,
    offsetVolY = 0,
    offsetVolZ = 0,
    showVolume,
    showXraySurface = true,
    withinFov = true,
  },
  ref
) {
  const mountRef = useRef(null);
  const stateRef = useRef(null);
  const fittedRef = useRef(false);

  useEffect(() => {
    const mount = mountRef.current;
    // StrictMode replays this effect with a fresh set of Three.js objects.
    // The replacement camera needs its own initial fit.
    fittedRef.current = false;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x373737);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, 100000);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    mount.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;

    const gridHelper = new THREE.GridHelper(1, 20, COLOR_BORDER, COLOR_BORDER);
    scene.add(gridHelper);

    const axesHelper = buildAxes();
    scene.add(axesHelper);

    const origin = new THREE.Mesh(
      new THREE.SphereGeometry(1, 12, 12),
      new THREE.MeshBasicMaterial({ color: 0xffffff })
    );
    scene.add(origin);

    const inclinedRig = buildRig(COLOR_INCLINED);
    const coplanarRig = buildRig(COLOR_COPLANAR);
    const gimbalRig = buildRig(COLOR_GIMBAL);
    scene.add(inclinedRig.group);
    scene.add(coplanarRig.group);
    scene.add(gimbalRig.group);

    const volume = buildVolumeCuboid();
    scene.add(volume.mesh);
    const outsideVolume = buildOutsideVolume();
    scene.add(outsideVolume);

    const resize = () => {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      // Flex/absolute layout can briefly report zero while the pane settles.
      // ResizeObserver will call us again once usable dimensions are available.
      if (w <= 0 || h <= 0) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(mount);

    let raf;
    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    stateRef.current = {
      scene,
      camera,
      renderer,
      controls,
      inclinedRig,
      coplanarRig,
      gimbalRig,
      volume,
      outsideVolume,
      gridHelper,
      axesHelper,
      origin,
      resizeObserver,
      raf,
    };

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      controls.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
      if (stateRef.current?.renderer === renderer) stateRef.current = null;
      fittedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    st.inclinedRig.group.visible = showInclined;
    st.coplanarRig.group.visible = showCoplanar;
    st.gimbalRig.group.visible = showGimbal;
  }, [showInclined, showCoplanar, showGimbal]);

  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    updateVolumeCuboid(st.volume, volRes, volX, volY, volZ, offsetVolX, offsetVolY, offsetVolZ);
  }, [volRes, volX, volY, volZ, offsetVolX, offsetVolY, offsetVolZ]);

  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    st.volume.mesh.visible = showVolume;
    st.outsideVolume.visible = showVolume;
  }, [showVolume]);

  // "Display x-ray surface" toggle: on = fully opaque solid surface, off =
  // translucent (matches the surface's original default look). depthWrite
  // must flip with it -- an opaque mesh that never writes depth still lets
  // objects behind it (e.g. the volume cuboid) show through regardless of
  // opacity, since the depth buffer never records it as occluding anything.
  //
  // The "outside common FOV" highlight mesh is deliberately drawn with
  // depthTest/depthWrite off (see buildOutsideVolume) so it stays visible
  // even nested inside other geometry -- but that means it always draws on
  // top of the cone too. Once the cone is a solid, opaque surface, a "solid"
  // object should properly occlude what's behind it, so depth testing is
  // re-enabled on the highlight mesh to match; with a translucent/see
  // -through cone it goes back to always-on-top so the highlight isn't lost.
  useEffect(() => {
    const st = stateRef.current;
    if (!st) return;
    for (const rig of [st.inclinedRig, st.coplanarRig, st.gimbalRig]) {
      rig.coneMesh.material.transparent = !showXraySurface;
      rig.coneMesh.material.opacity = showXraySurface ? 1.0 : 0.1;
      rig.coneMesh.material.depthWrite = showXraySurface;
      rig.coneMesh.visible = true;
    }
    st.outsideVolume.material.depthTest = showXraySurface;
    st.outsideVolume.material.depthWrite = showXraySurface;
  }, [showXraySurface]);

  // Instant: repositions rigs/camera-fit extents to the current theta. Kept
  // free of the expensive outside-volume voxel scan so dragging theta never
  // stalls waiting on tens of millions of point-in-cone tests.
  useEffect(() => {
    const st = stateRef.current;
    if (!st || inclinedVecs.length === 0) return;
    // Mirrors buildAngles' inclusive [angleStart, angleEnd] spacing so theta
    // (an absolute angle in degrees) maps back to the matching frame index.
    const span = angleEnd - angleStart;
    const frac = span !== 0 ? (theta - angleStart) / span : 0;
    const idx = numAngles > 1
      ? Math.min(numAngles - 1, Math.max(0, Math.round(frac * (numAngles - 1))))
      : 0;
    updateRig(st.inclinedRig, inclinedVecs[idx], halfU, halfV);
    updateRig(st.coplanarRig, coplanarVecs[idx], halfU, halfV);
    updateRig(st.gimbalRig, gimbalVecs[idx], halfU, halfV);

    const { nearExtent, farExtent } = extentsOf(
      [...inclinedVecs, ...coplanarVecs, ...gimbalVecs],
      halfU,
      halfV
    );
    st.nearExtent = nearExtent;
    st.farExtent = farExtent;

    // Fit the camera once per mount to the near (source/object-plane) region
    // rather than the whole detector orbit: with real CBCT params SOD is
    // often 1000x smaller than SDD, so a whole-scene fit leaves the cone-tip
    // sub-pixel. Default view is tight on the source; the FOV/Overall buttons
    // (or manual scroll) reveal the detector, placed correctly at true scale.
    if (!fittedRef.current && nearExtent > 0) {
      fittedRef.current = true;
      fitCamera(st, nearExtent, farExtent, "near");
    }
  }, [inclinedVecs, coplanarVecs, gimbalVecs, theta, numAngles, angleStart, angleEnd, halfU, halfV]);

  // Expensive: tests every voxel corner against every acquisition angle's
  // cone (vecs.every(...) inside updateOutsideVolume), i.e. the region
  // outside the common FOV across the *whole* rotation sweep -- independent
  // of theta, so theta is deliberately not a dependency here. Debounced
  // ~200ms after a relevant input settles so slider drags stay instant.
  useEffect(() => {
    const st = stateRef.current;
    if (!st || inclinedVecs.length === 0) return;
    const id = setTimeout(() => {
      const visibleVecs = [];
      if (showInclined) visibleVecs.push(...inclinedVecs);
      if (showCoplanar) visibleVecs.push(...coplanarVecs);
      if (showGimbal) visibleVecs.push(...gimbalVecs);
      updateOutsideVolume(
        st.outsideVolume,
        visibleVecs,
        halfU,
        halfV,
        volRes,
        volX,
        volY,
        volZ,
        offsetVolX,
        offsetVolY,
        offsetVolZ
      );
    }, 200);
    return () => clearTimeout(id);
  }, [
    inclinedVecs,
    coplanarVecs,
    gimbalVecs,
    showInclined,
    showCoplanar,
    showGimbal,
    halfU,
    halfV,
    volRes,
    volX,
    volY,
    volZ,
    offsetVolX,
    offsetVolY,
    offsetVolZ,
  ]);

  useImperativeHandle(ref, () => ({
    focusFov() {
      const st = stateRef.current;
      if (!st || !st.nearExtent) return;
      fitCamera(st, st.nearExtent, st.farExtent, "near");
    },
    focusOverall() {
      const st = stateRef.current;
      if (!st || !st.nearExtent) return;
      fitCamera(st, st.nearExtent, st.farExtent, "overall");
    },
    focusDirection(direction) {
      const st = stateRef.current;
      if (!st || !st.nearExtent) return;
      fitCamera(st, st.nearExtent, st.farExtent, "near", direction);
    },
  }));

  return (
    <div className="canvas-mount" ref={mountRef}>
      <div className="canvas-label">Geometry</div>
      <div className="canvas-legend">
        <span className="legend-item"><span className="legend-dot" style={{ background: "#f97362" }} /> Inclined</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: "#5EEAD4" }} /> Coplanar</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: "#F5A623" }} /> Gimbal Lock</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: "#25ADDE" }} /> Volume</span>
        <span className="legend-item"><span className="legend-dot" style={{ background: "#ff3158" }} /> Outside common FOV</span>
      </div>
      <div className="canvas-fov-status" style={{ color: withinFov ? COLOR_WITHIN_FOV : COLOR_OUT_OF_FOV }}>
        {withinFov ? "Within FOV" : "Out of FOV"}
      </div>
    </div>
  );
});

export default Scene3D;
